import Phaser from "phaser";
import { GAME, COLORS } from "../config";
import { addFullscreenButton } from "../ui/Fullscreen";
import { getMpClient, type PlayerInfo } from "../net/MultiplayerClient";
import { getStoredCredentials } from "../net/playerIdentity";

interface MenuOption {
  label: string;
  desc: string;
  action: () => void;
  color: string;
  requireAuth?: boolean;
}

const IDLE_TIMEOUT_MS = 30_000;     // 30 секунд бездействия → screensaver

export class MainMenuScene extends Phaser.Scene {
  private selectedIdx = 0;
  private options: MenuOption[] = [];
  private optionTexts: Phaser.GameObjects.Text[] = [];
  private mpUnsub?: () => void;
  private profileBadge?: Phaser.GameObjects.Text;
  private idleTimer?: Phaser.Time.TimerEvent;

  constructor() { super("MainMenu"); }

  preload() {
    if (!this.textures.exists("logo")) {
      this.load.svg("logo", "assets/logo.svg", { width: 520, height: 220 });
    }
  }

  create() {
    const { WIDTH, HEIGHT } = GAME;

    const bg = this.add.graphics();
    bg.fillGradientStyle(COLORS.BG_TOP, COLORS.BG_TOP, COLORS.BG_BOTTOM, COLORS.BG_BOTTOM, 1);
    bg.fillRect(0, 0, WIDTH, HEIGHT);

    if (this.textures.exists("logo")) {
      const logo = this.add.image(WIDTH / 2, HEIGHT * 0.22, "logo").setOrigin(0.5);
      const maxW = WIDTH * 0.50;
      const maxH = HEIGHT * 0.36;
      const scale = Math.min(maxW / logo.width, maxH / logo.height);
      logo.setScale(scale);
    } else {
      this.add.text(WIDTH / 2, HEIGHT * 0.18, "BANZAI", {
        fontFamily: "Arial Black", fontSize: "72px", color: "#ffffff",
        stroke: "#c41e3a", strokeThickness: 8,
      }).setOrigin(0.5);
      this.add.text(WIDTH / 2, HEIGHT * 0.28, "FIGHTERS", {
        fontFamily: "Arial Black", fontSize: "36px", color: "#c41e3a",
        stroke: "#000000", strokeThickness: 4,
      }).setOrigin(0.5);
    }

    this.options = [
      {
        label: "🥋 ТРЕНИРОВКА",
        desc: "Один игрок — бой против AI",
        color: "#ffd700",
        action: () => this.scene.start("Training"),
      },
      {
        label: "⚔ ТУРНИР (2 ИГРОКА)",
        desc: "Онлайн-бой: только для залогиненных",
        color: "#88ff88",
        action: () => this.scene.start("Friends"),
        requireAuth: true,
      },
      {
        label: "🏆 ТУРНИРНАЯ ТАБЛИЦА",
        desc: "Топ-10 бойцов клуба",
        color: "#ff99cc",
        action: () => this.scene.start("Leaderboard"),
      },
      {
        label: "⚙ НАСТРОЙКИ",
        desc: "Аккаунт, смена логина/пароля",
        color: "#88ccff",
        action: () => this.scene.start("Settings"),
      },
    ];

    const optY0 = HEIGHT * 0.48;
    const optStep = 42;
    this.options.forEach((opt, i) => {
      const y = optY0 + i * optStep;
      const text = this.add.text(WIDTH / 2, y, opt.label, {
        fontFamily: "Arial Black", fontSize: "22px", color: opt.color,
        stroke: "#000", strokeThickness: 4,
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });
      text.on("pointerdown", () => this.activate(i));
      text.on("pointerover", () => { this.selectedIdx = i; this.refreshSelection(); this.bumpIdle(); });
      this.optionTexts.push(text);
    });

    const descText = this.add.text(WIDTH / 2, HEIGHT - 60, "", {
      fontFamily: "Arial", fontSize: "14px", color: "#888888",
    }).setOrigin(0.5);

    this.refreshSelection = () => {
      this.optionTexts.forEach((t, i) => {
        t.setScale(i === this.selectedIdx ? 1.15 : 1.0);
        t.setAlpha(i === this.selectedIdx ? 1 : 0.6);
      });
      descText.setText(this.options[this.selectedIdx].desc);
    };
    this.refreshSelection();

    const KB = this.input.keyboard!;
    const wrap = (d: number) => { this.selectedIdx = (this.selectedIdx + this.options.length + d) % this.options.length; this.refreshSelection(); this.bumpIdle(); };
    KB.on("keydown-UP",   () => wrap(-1));
    KB.on("keydown-DOWN", () => wrap( 1));
    KB.on("keydown-W",    () => wrap(-1));
    KB.on("keydown-S",    () => wrap( 1));
    KB.on("keydown-ENTER",() => this.activate(this.selectedIdx));
    KB.on("keydown-SPACE",() => this.activate(this.selectedIdx));
    KB.on("keydown-F",    () => this.activate(this.selectedIdx));
    KB.on("keydown-K",    () => this.activate(this.selectedIdx));

    this.add.text(WIDTH / 2, HEIGHT - 24, "↑↓ или W/S — выбор    |    ENTER/F/K — старт", {
      fontFamily: "monospace", fontSize: "11px", color: "#555555",
    }).setOrigin(0.5);

    this.profileBadge = this.add.text(WIDTH - 12, HEIGHT - 12, "Гость (войди в Настройках)", {
      fontFamily: "Arial Black", fontSize: "12px", color: "#888888",
    }).setOrigin(1, 1);

    addFullscreenButton(this);

    // any-input bumps idle
    this.input.on("pointerdown", () => this.bumpIdle());
    this.input.keyboard!.on("keydown", () => this.bumpIdle());

    this.connectAndAutoLogin();
    this.bumpIdle();
  }

  private connectAndAutoLogin() {
    const mp = getMpClient();
    this.mpUnsub = mp.on(e => this.handleEvent(e));

    const onAuthed = () => this.refreshProfileBadge();

    const tryAuth = () => {
      if (mp.isAuthed()) { onAuthed(); return; }
      const creds = getStoredCredentials();
      if (creds) {
        mp.loginUser(creds.login, creds.password);
      } else {
        if (this.profileBadge) this.profileBadge.setText("Гость (войди в Настройках)").setColor("#888888");
      }
    };

    if (mp.isOpen()) tryAuth();
    else {
      mp.connect().then(tryAuth).catch(() => {
        if (this.profileBadge) this.profileBadge.setText("Сервер недоступен").setColor("#ff6666");
      });
    }
  }

  private refreshProfileBadge() {
    const mp = getMpClient();
    if (!this.profileBadge) return;
    if (mp.isAuthed()) {
      this.profileBadge
        .setText(`${mp.login}  W:${mp.wins}  L:${mp.losses}`)
        .setColor("#ffd700");
    } else {
      this.profileBadge.setText("Гость (войди в Настройках)").setColor("#888888");
    }
  }

  private activate(i: number) {
    const opt = this.options[i];
    this.bumpIdle();
    if (opt.requireAuth && !getMpClient().isAuthed()) {
      this.scene.start("Settings", { required: true });
      return;
    }
    opt.action();
  }

  private handleEvent(e: any) {
    if (e.type === "logged_in" || e.type === "registered" || e.type === "stats" || e.type === "login_updated") {
      this.refreshProfileBadge();
    } else if (e.type === "friend_invite") {
      this.showInvitePrompt(e.from as PlayerInfo, e.roomCode as string);
    }
  }

  private showInvitePrompt(from: PlayerInfo, roomCode: string) {
    const { WIDTH, HEIGHT } = GAME;
    const overlay = this.add.rectangle(WIDTH / 2, HEIGHT / 2, WIDTH, HEIGHT, 0x000000, 0.75).setDepth(2000);
    const card = this.add.rectangle(WIDTH / 2, HEIGHT / 2, 480, 200, 0x1a0808, 1)
      .setStrokeStyle(3, 0xffd700).setDepth(2001);
    const title = this.add.text(WIDTH / 2, HEIGHT / 2 - 60, "ВЫЗОВ В БОЙ!", {
      fontFamily: "Arial Black", fontSize: "26px", color: "#ffd700",
      stroke: "#c41e3a", strokeThickness: 4,
    }).setOrigin(0.5).setDepth(2002);
    const who = this.add.text(WIDTH / 2, HEIGHT / 2 - 18, `${from.login}\nзовёт сразиться`, {
      fontFamily: "Arial Black", fontSize: "16px", color: "#ffffff", align: "center",
    }).setOrigin(0.5).setDepth(2002);

    const accept = this.add.rectangle(WIDTH / 2 - 100, HEIGHT / 2 + 50, 160, 48, 0x2a6a2a)
      .setStrokeStyle(3, 0x88ff88).setDepth(2001).setInteractive({ useHandCursor: true });
    const acceptT = this.add.text(WIDTH / 2 - 100, HEIGHT / 2 + 50, "✓ ПРИНЯТЬ", {
      fontFamily: "Arial Black", fontSize: "18px", color: "#ffffff",
    }).setOrigin(0.5).setDepth(2002);
    const decline = this.add.rectangle(WIDTH / 2 + 100, HEIGHT / 2 + 50, 160, 48, 0x6a2a2a)
      .setStrokeStyle(3, 0xff8888).setDepth(2001).setInteractive({ useHandCursor: true });
    const declineT = this.add.text(WIDTH / 2 + 100, HEIGHT / 2 + 50, "✗ ОТКЛОНИТЬ", {
      fontFamily: "Arial Black", fontSize: "18px", color: "#ffffff",
    }).setOrigin(0.5).setDepth(2002);

    const cleanup = () => {
      overlay.destroy(); card.destroy(); title.destroy(); who.destroy();
      accept.destroy(); acceptT.destroy(); decline.destroy(); declineT.destroy();
    };
    accept.on("pointerdown", () => { cleanup(); this.scene.start("Lobby", { mode: "join", autoCode: roomCode }); });
    decline.on("pointerdown", cleanup);
  }

  // ── idle screensaver ───────────────────────────────────────────────────────
  private bumpIdle() {
    this.idleTimer?.remove();
    this.idleTimer = this.time.delayedCall(IDLE_TIMEOUT_MS, () => {
      this.scene.start("Leaderboard", { screensaver: true });
    });
  }

  shutdown() {
    this.mpUnsub?.();
    this.idleTimer?.remove();
  }

  private refreshSelection: () => void = () => {};
}
